import api from '../api';

/**
 * 前端权限控制工具
 * 提供权限检查、菜单控制等功能
 */

class PermissionManager {
    constructor() {
        this.permissions = {};
        this.modules = [];
        this.user = null;
        this.isLoaded = false;
    }

    /**
     * 加载用户权限信息
     */
    async loadPermissions() {
        try {
            const response = await api.get('/api/permissions/');
            this.permissions = response.data.permissions || {};
            this.user = response.data.user || {
                id: response.data.user_id,
                username: response.data.username,
                role: response.data.role,
                group: response.data.group,
            };
            this.isLoaded = true;
            return true;
        } catch (error) {
            console.error('加载权限信息失败:', error);
            return false;
        }
    }

    /**
     * 加载可访问模块列表
     */
    async loadAccessibleModules() {
        try {
            const response = await api.get('/api/accessible_modules/');
            this.modules = response.data.modules;
            return this.modules;
        } catch (error) {
            console.error('加载可访问模块失败:', error);
            return [];
        }
    }

    /**
     * 检查用户是否有指定权限
     * @param {string} module - 模块名
     * @param {string} action - 操作名
     * @returns {boolean}
     */
    hasPermission(module, action) {
        if (!this.isLoaded) {
            console.warn('权限信息未加载，请先调用 loadPermissions()');
            return false;
        }

        // 超级管理员拥有所有权限
        if (this.user && this.user.role === 'superadmin') {
            console.log(`超级管理员 ${this.user.username} 拥有所有权限`);
            return true;
        }

        return this.permissions[module] && this.permissions[module][action] === true;
    }

    /**
     * 检查用户是否有模块的任何权限
     * @param {string} module - 模块名
     * @returns {boolean}
     */
    hasModulePermission(module) {
        if (!this.isLoaded) {
            return false;
        }

        if (this.user && this.user.role === 'superadmin') {
            return true;
        }

        const modulePermissions = this.permissions[module];
        if (!modulePermissions) {
            return false;
        }

        return Object.values(modulePermissions).some(hasPermission => hasPermission);
    }

    /**
     * 获取用户可访问的模块列表
     * @returns {Array}
     */
    getAccessibleModules() {
        return this.modules;
    }

    /**
     * 检查用户是否可以访问指定模块
     * @param {string} moduleKey - 模块键名
     * @returns {boolean}
     */
    canAccessModule(moduleKey) {
        return this.modules.some(module => module.key === moduleKey);
    }

    /**
     * 获取用户信息
     * @returns {Object|null}
     */
    getUser() {
        return this.user;
    }

    /**
     * 检查用户角色
     * @param {string|Array} roles - 角色名或角色数组
     * @returns {boolean}
     */
    hasRole(roles) {
        if (!this.user) {
            return false;
        }

        if (Array.isArray(roles)) {
            return roles.includes(this.user.role);
        }

        return this.user.role === roles;
    }

    /**
     * 检查是否为管理员
     * @returns {boolean}
     */
    isAdmin() {
        return this.hasRole(['admin', 'manager', 'superadmin']);
    }

    /**
     * 检查是否为超级管理员
     * @returns {boolean}
     */
    isSuperAdmin() {
        return this.hasRole('superadmin');
    }

    /**
     * 检查是否为学生
     * @returns {boolean}
     */
    isStudent() {
        return this.hasRole(['student', 'teacher']);
    }

    /**
     * 重置权限信息
     */
    reset() {
        this.permissions = {};
        this.modules = [];
        this.user = null;
        this.isLoaded = false;
    }

    /**
     * 从缓存恢复权限状态（SchoolDashboard 使用）
     */
    restoreFromCache(cachedData) {
        if (!cachedData) {
            return;
        }
        this.permissions = cachedData.permissions || {};
        this.user = cachedData.user || null;
        this.modules = cachedData.modules || [];
        this.isLoaded = true;
    }

    /**
     * 导出可缓存的权限数据
     */
    getCacheData() {
        if (!this.isLoaded) {
            return null;
        }
        return {
            permissions: this.permissions,
            user: this.user,
            modules: this.modules,
        };
    }
}

// 创建全局权限管理器实例
const permissionManager = new PermissionManager();

export default permissionManager;

/**
 * 权限检查高阶组件
 * @param {string} module - 模块名
 * @param {string} action - 操作名
 * @param {React.Component} Component - 要包装的组件
 * @param {React.Component} FallbackComponent - 无权限时显示的组件
 */
export function withPermission(module, action, Component, FallbackComponent = null) {
    return function PermissionWrapper(props) {
        const hasPermission = permissionManager.hasPermission(module, action);
        
        if (hasPermission) {
            return <Component {...props} />;
        }
        
        return FallbackComponent ? <FallbackComponent {...props} /> : null;
    };
}

/**
 * 权限按钮组件
 * @param {Object} props - 组件属性
 * @param {string} props.module - 模块名
 * @param {string} props.action - 操作名
 * @param {React.ReactNode} props.children - 按钮内容
 * @param {Object} props.buttonProps - 按钮属性
 */
export function PermissionButton({ module, action, children, buttonProps = {}, ...props }) {
    const hasPermission = permissionManager.hasPermission(module, action);
    
    if (!hasPermission) {
        return null;
    }
    
    return (
        <button {...buttonProps} {...props}>
            {children}
        </button>
    );
}

/**
 * 权限链接组件
 * @param {Object} props - 组件属性
 * @param {string} props.module - 模块名
 * @param {string} props.action - 操作名
 * @param {React.ReactNode} props.children - 链接内容
 * @param {Object} props.linkProps - 链接属性
 */
export function PermissionLink({ module, action, children, linkProps = {}, ...props }) {
    const hasPermission = permissionManager.hasPermission(module, action);
    
    if (!hasPermission) {
        return null;
    }
    
    return (
        <a {...linkProps} {...props}>
            {children}
        </a>
    );
}
